
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PoundSterling, TrendingDown, TrendingUp, AlertCircle, Download, Plus } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CostItem {
  id: string;
  category: string;
  description: string;
  budgeted: number;
  actual: number;
  variance: number;
  status: 'on-budget' | 'over-budget' | 'under-budget' | 'at-risk';
  date: string;
}

export default function CostTracking() {
  const [selectedPeriod, setSelectedPeriod] = useState("current-month");
  const [selectedProject, setSelectedProject] = useState("all");

  const [costItems] = useState<CostItem[]>([
    {
      id: '1',
      category: 'Personnel',
      description: 'Development team salaries',
      budgeted: 120000,
      actual: 118500,
      variance: -1500,
      status: 'under-budget',
      date: '2024-01-31'
    },
    {
      id: '2',
      category: 'Equipment',
      description: 'Hardware and software licenses',
      budgeted: 50000,
      actual: 55200,
      variance: 5200,
      status: 'over-budget',
      date: '2024-01-31'
    },
    {
      id: '3',
      category: 'Materials',
      description: 'Construction materials',
      budgeted: 200000,
      actual: 195000,
      variance: -5000,
      status: 'under-budget',
      date: '2024-01-31'
    },
    {
      id: '4',
      category: 'Services',
      description: 'External consulting services',
      budgeted: 75000,
      actual: 82000,
      variance: 7000,
      status: 'over-budget',
      date: '2024-01-31'
    },
    {
      id: '5',
      category: 'Travel',
      description: 'Business travel expenses',
      budgeted: 15000,
      actual: 18500,
      variance: 3500,
      status: 'at-risk',
      date: '2024-01-31'
    }
  ]);

  const totalBudgeted = costItems.reduce((sum, item) => sum + item.budgeted, 0);
  const totalActual = costItems.reduce((sum, item) => sum + item.actual, 0);
  const totalVariance = totalActual - totalBudgeted;
  const budgetUtilization = (totalActual / totalBudgeted) * 100;

  const getStatusColor = (status: CostItem['status']) => {
    switch (status) {
      case 'on-budget': return 'bg-green-100 text-green-800';
      case 'under-budget': return 'bg-blue-100 text-blue-800';
      case 'over-budget': return 'bg-red-100 text-red-800';
      case 'at-risk': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVarianceColor = (variance: number) => {
    if (variance > 0) return 'text-red-600';
    if (variance < 0) return 'text-green-600';
    return 'text-gray-600';
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <PoundSterling className="h-8 w-8 text-primary" />
            Cost Tracking
          </h1>
          <p className="text-muted-foreground">
            Monitor project costs, budget utilization, and financial performance metrics
          </p>
        </div>

        <div className="flex gap-4 mb-6">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current-month">Current Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="current-quarter">Current Quarter</SelectItem>
              <SelectItem value="last-quarter">Last Quarter</SelectItem>
              <SelectItem value="year-to-date">Year to Date</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              <SelectItem value="project-1">New Office Building</SelectItem>
              <SelectItem value="project-2">IT System Upgrade</SelectItem>
              <SelectItem value="project-3">Manufacturing Equipment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="budget">Budget Analysis</TabsTrigger>
            <TabsTrigger value="forecasting">Forecasting</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">£{(totalBudgeted / 1000).toFixed(0)}K</div>
                    <p className="text-sm text-muted-foreground">Total Budget</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">£{(totalActual / 1000).toFixed(0)}K</div>
                    <p className="text-sm text-muted-foreground">Actual Spend</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${getVarianceColor(totalVariance)}`}>
                      {totalVariance > 0 ? '+' : ''}£{(totalVariance / 1000).toFixed(0)}K
                    </div>
                    <p className="text-sm text-muted-foreground">Variance</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{budgetUtilization.toFixed(1)}%</div>
                    <p className="text-sm text-muted-foreground">Budget Utilization</p>
                    <Progress value={budgetUtilization} className="mt-2" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Cost Breakdown by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {costItems.map((item) => {
                      const percentage = (item.actual / totalActual) * 100;
                      return (
                        <div key={item.id} className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{item.category}</span>
                            <div className="text-right">
                              <div>£{item.actual.toLocaleString()}</div>
                              <div className="text-sm text-muted-foreground">{percentage.toFixed(1)}%</div>
                            </div>
                          </div>
                          <Progress value={percentage} className="w-full" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Budget Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {costItems
                      .filter(item => item.status === 'over-budget' || item.status === 'at-risk')
                      .map((item) => (
                        <div key={item.id} className="p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{item.category}</p>
                              <p className="text-xs text-muted-foreground">{item.description}</p>
                              <p className="text-xs mt-1">
                                Budget: £{item.budgeted.toLocaleString()} | 
                                Actual: £{item.actual.toLocaleString()}
                              </p>
                            </div>
                            <Badge className={getStatusColor(item.status)}>
                              {item.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="budget" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Detailed Budget Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Category</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-right p-3">Budgeted</th>
                        <th className="text-right p-3">Actual</th>
                        <th className="text-right p-3">Variance</th>
                        <th className="text-center p-3">Status</th>
                        <th className="text-right p-3">% of Budget</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costItems.map((item) => (
                        <tr key={item.id} className="border-b">
                          <td className="p-3 font-medium">{item.category}</td>
                          <td className="p-3 text-sm text-muted-foreground">{item.description}</td>
                          <td className="p-3 text-right">£{item.budgeted.toLocaleString()}</td>
                          <td className="p-3 text-right">£{item.actual.toLocaleString()}</td>
                          <td className={`p-3 text-right font-medium ${getVarianceColor(item.variance)}`}>
                            {item.variance > 0 ? '+' : ''}£{item.variance.toLocaleString()}
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={getStatusColor(item.status)}>
                              {item.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            {((item.actual / item.budgeted) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 font-semibold">
                        <td className="p-3" colSpan={2}>Total</td>
                        <td className="p-3 text-right">£{totalBudgeted.toLocaleString()}</td>
                        <td className="p-3 text-right">£{totalActual.toLocaleString()}</td>
                        <td className={`p-3 text-right ${getVarianceColor(totalVariance)}`}>
                          {totalVariance > 0 ? '+' : ''}£{totalVariance.toLocaleString()}
                        </td>
                        <td className="p-3"></td>
                        <td className="p-3 text-right">{budgetUtilization.toFixed(1)}%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="forecasting" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Cost Forecast
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Projected Total Cost:</span>
                      <span className="text-lg font-bold">£{(totalActual * 1.15).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Budget Remaining:</span>
                      <span className={`text-lg font-bold ${totalVariance < 0 ? 'text-green-600' : 'text-red-600'}`}>
                        £{Math.abs(totalBudgeted - totalActual).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Burn Rate (Monthly):</span>
                      <span className="text-lg font-bold">£{(totalActual / 12).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Projected Completion:</span>
                      <span className="text-lg font-bold">June 2024</span>
                    </div>

                    <div className="pt-4 border-t">
                      <h4 className="font-semibold mb-3">Monthly Forecast</h4>
                      <div className="space-y-2">
                        {['Feb 2024', 'Mar 2024', 'Apr 2024', 'May 2024'].map((month, index) => {
                          const forecastAmount = totalActual * (1 + (index + 1) * 0.08);
                          return (
                            <div key={month} className="flex justify-between">
                              <span>{month}:</span>
                              <span>£{forecastAmount.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5" />
                    Cost Optimization
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-800">Potential Savings</h4>
                      <p className="text-sm text-green-700 mt-1">
                        Equipment category shows 10.4% over budget. Consider renegotiating contracts.
                      </p>
                      <p className="font-medium text-green-800 mt-2">Potential saving: £5,200</p>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-800">Efficiency Gains</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Personnel costs are 1.25% under budget. Good resource management.
                      </p>
                      <p className="font-medium text-blue-800 mt-2">Current saving: £1,500</p>
                    </div>

                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <h4 className="font-semibold text-yellow-800">Risk Areas</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Travel expenses trending 23% over budget. Monitor closely.
                      </p>
                      <p className="font-medium text-yellow-800 mt-2">At risk: £3,500</p>
                    </div>

                    <div className="pt-4">
                      <Button className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Cost Optimization Plan
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Cost Tracking Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Budget vs Actual Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Cost Variance Analysis</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Forecast Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Executive Summary</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Category Analysis</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Optimization Report</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
